import React from "react";

/**
 * JuicyOrange — hand-drawn SVG "healthy orange" for the Orange Optimise
 * promo modal. Not the canonical OrangeScoreBadge (that lives inside the
 * product). This one is a premium, glossy, promotional graphic only.
 *
 *   • Radial fill: warm peel gradient (deep vermillion → orange → sunny highlight)
 *   • Peel dimple noise via displaced dots
 *   • Curved specular highlight
 *   • Small green leaf + brown stem
 *   • Score number rendered ON the fruit
 *
 * No external assets. Scales cleanly at any size.
 */
export default function JuicyOrange({ score = 87, size = 140, testId }) {
  const s = size;
  return (
    <div
      data-testid={testId}
      style={{ width: s, height: s, position: "relative" }}
      className="inline-block select-none"
      aria-label={`Orange Score ${score}`}
    >
      <svg viewBox="0 0 200 200" width={s} height={s} aria-hidden="true">
        <defs>
          <radialGradient id="jo-peel" cx="35%" cy="30%" r="80%">
            <stop offset="0%"   stopColor="#FFD08A" />
            <stop offset="18%"  stopColor="#FFB05A" />
            <stop offset="52%"  stopColor="#FF7A1F" />
            <stop offset="82%"  stopColor="#E85A0B" />
            <stop offset="100%" stopColor="#B03A00" />
          </radialGradient>
          <radialGradient id="jo-shadow" cx="50%" cy="98%" r="55%">
            <stop offset="0%"   stopColor="#000" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="jo-highlight" cx="30%" cy="22%" r="35%">
            <stop offset="0%"   stopColor="#FFF3D8" stopOpacity="0.95" />
            <stop offset="60%"  stopColor="#FFE1A8" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#FFE1A8" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="jo-leaf" cx="30%" cy="30%" r="80%">
            <stop offset="0%"   stopColor="#7FD86A" />
            <stop offset="60%"  stopColor="#3FA83A" />
            <stop offset="100%" stopColor="#256E1F" />
          </radialGradient>
          <pattern id="jo-dimples" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="4"  cy="4"  r="0.9" fill="#8A2E00" opacity="0.35"/>
            <circle cx="10" cy="9"  r="0.7" fill="#8A2E00" opacity="0.28"/>
            <circle cx="6"  cy="11" r="0.6" fill="#5A1E00" opacity="0.25"/>
          </pattern>
        </defs>

        {/* soft ground shadow */}
        <ellipse cx="100" cy="182" rx="60" ry="8" fill="url(#jo-shadow)"/>

        {/* fruit body */}
        <circle cx="100" cy="102" r="82" fill="url(#jo-peel)"/>
        {/* peel dimples */}
        <circle cx="100" cy="102" r="82" fill="url(#jo-dimples)"/>
        {/* inner rim (a tiny darker ring) */}
        <circle cx="100" cy="102" r="82" fill="none" stroke="#8A2E00" strokeOpacity="0.35" strokeWidth="2"/>
        {/* specular highlight */}
        <ellipse cx="72" cy="66" rx="34" ry="22" fill="url(#jo-highlight)"/>
        {/* small crescent glossy stroke */}
        <path d="M 42 82 Q 68 46 108 46" fill="none" stroke="#FFF3D8" strokeOpacity="0.55" strokeWidth="4" strokeLinecap="round"/>

        {/* stem */}
        <path d="M 100 22 C 102 12 108 8 116 6"
              fill="none" stroke="#5A3B1E" strokeWidth="4" strokeLinecap="round"/>
        {/* leaf */}
        <path d="M 118 6 C 148 -4 172 14 158 42 C 142 34 122 24 118 6 Z"
              fill="url(#jo-leaf)"/>
        <path d="M 122 12 C 140 22 152 30 156 38"
              fill="none" stroke="#256E1F" strokeOpacity="0.7" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>

      {/* Score overlay */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: "8%",
        }}
      >
        <span
          style={{
            fontWeight: 900,
            fontSize: Math.round(s * 0.36),
            lineHeight: 1,
            color: "#fff",
            letterSpacing: "-0.03em",
            textShadow: "0 2px 10px rgba(120, 30, 0, 0.55), 0 1px 0 rgba(0,0,0,0.35)",
          }}
        >
          {score}
        </span>
      </div>
    </div>
  );
}
